import { AnimatePresence, motion } from 'framer-motion'
import { DURATION, EASE_IN, EASE_OUT } from '@workflow/ui'
import { setCookieChoice, useCookieChoice } from './cookies'
import { navigate } from '@/app/router'

/**
 * The cookie question, asked once per browser on the public door: what the
 * studio sets, in plain words, with the policy one click away. Accepting all
 * loads Google's sign-in layer; essentials keep the session and Cloudflare's
 * security cookies only. Silence leaves the banner up — nothing optional
 * loads until it is chosen.
 */
export function CookieBanner() {
  const choice = useCookieChoice()
  const unanswered = choice === null

  return (
    <AnimatePresence>
      {unanswered ? (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie choice"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_OUT, delay: 0.8 } }}
          exit={{ opacity: 0, y: 8, transition: { duration: DURATION.fast, ease: EASE_IN } }}
          className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-md rounded-2xl border border-hairline bg-surface p-5 shadow-modal"
        >
          <p className="text-ui font-medium text-foreground">Cookies, plainly</p>
          <p className="mt-1.5 text-caption leading-relaxed text-muted-foreground">
            The session and security cookies keep you signed in — always on. Google's sign-in
            layer is optional and loads only if you accept it. No telemetry, no advertising.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCookieChoice({ optional: true })}
              className="rounded-full bg-foreground px-4 py-2 text-caption font-medium text-surface transition-opacity duration-fast outline-none hover:opacity-90 focus-visible:ring-1 focus-visible:ring-foreground"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => setCookieChoice({ optional: false })}
              className="rounded-full border border-hairline px-4 py-2 text-caption font-medium text-foreground transition-colors duration-fast outline-none hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground"
            >
              Essentials only
            </button>
            <button
              type="button"
              onClick={() => navigate({ name: 'cookies' })}
              className="px-2 py-2 text-caption text-muted-foreground underline-offset-4 transition-colors duration-fast outline-none hover:text-foreground hover:underline focus-visible:ring-1 focus-visible:ring-foreground"
            >
              Cookie policy
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
